import React from 'react'

import Or from 'components/Or'
import Button from 'components/Button'
import T from 'components/FormattedMessage'
import TextInput from 'components/TextInput'
import ActionContext from 'components/ActionContext'
import InviteFriendsButton from 'components/InviteFriendsButton'

import SearchIcon from '@material-ui/icons/Search'
import Typography from '@material-ui/core/Typography'
import { withStyles, createStyles, WithStyles, Theme } from '@material-ui/core'

import { injectIntl, WrappedComponentProps } from 'react-intl'
import { FriendsResponse, UserListResponse } from 'protos'
import { Action } from 'types'

import UserSearchResult from './components/UserSearchResult'
import FacebookConnect from './components/FacebookConnect'
import MyFriends from './components/MyFriends'

import facebookIconUrl from './assets/facebook.svg'

interface Props {
  getUserFriends: () => Promise<FriendsResponse>
  searchUsers: (text: string) => Promise<UserListResponse>
  loginToFacebook: () => Promise<any> // tslint:disable-line:no-any
  onChallenge: (userId: string) => any // tslint:disable-line:no-any
}

type State = {
  friends: Action<FriendsResponse | null>
  searchResult: Action<UserListResponse | null>
  searchText: string
}

const styles = (theme: Theme) => createStyles({
  headline: {
    marginBottom: theme.spacing() * 2,
  },
  searchInputContainer: {
    margin: theme.spacing() * 2,
  },
  searchIcon: {
    width: 32,
    height: 32,
    marginLeft: theme.spacing(),
    color: theme.palette.grey['500'],
  },
  facebookIcon: {
    width: 16,
    height: 16,
    display: 'block',
    float: 'left',
    marginRight: theme.spacing(),
  },
  emptyStateButton: {
    width: 260,
    margin: 'auto',
    display: 'block',
    marginTop: theme.spacing() * 2,
  },
})

type InjectedProps = Props & WithStyles<typeof styles> & WrappedComponentProps

class MyFriendsTab extends React.Component<InjectedProps, State> {
  constructor(props: InjectedProps) {
    super(props)
    this.state = {
      friends: { isLoading: true },
      searchResult: { isLoading: true },
      searchText: '',
    }

    this.requestFriends()
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { searchText } = this.state

    if (prevState.searchText !== searchText) {
      if (searchText.length > 0) {
        this.requestSearchUsers()
      } else {
        this.requestFriends()
      }
    }
  }

  private requestFriends() {
    this.props.getUserFriends()
      .then((result: FriendsResponse) => {
        this.setState({ friends: { isLoading: false, result } })
      })
      .catch((err: Error) => {
        this.setState({ friends: { isLoading: false, errorCode: err.message } })
      })
  }

  private requestSearchUsers() {
    this.props.searchUsers(this.state.searchText)
      .then((result: UserListResponse) => {
        this.setState({ searchResult: { isLoading: false, result } })
      })
      .catch((err: Error) => {
        this.setState({ searchResult: { isLoading: false, errorCode: err.message } })
      })
  }

  private onFacebookConnectClick = async () => {
    await this.props.loginToFacebook()
    this.requestFriends()
  }

  private shouldRenderEmptyState = () => {
    const { result } = this.state.friends

    return Boolean(result && result.request.length + result.friend.length === 0)
  }

  private isFacebookConnected = () => {
    const { friends } = this.state
    return friends.result && friends.result.facebookStatus === FriendsResponse.FacebookStatus.FBS_SUCCESS
  }

  private renderFriends = () => {
    const { classes, onChallenge } = this.props
    const { result } = this.state.friends
    const { request, friend } = result!

    return this.shouldRenderEmptyState()
      ? (
        <>
          <Typography variant="h5" align="center" className={classes.headline}>
            <T id="friends_empty" />
          </Typography>
          <Typography variant="subtitle1" color="textSecondary" align="center">
            <T id="friends_empty_message" />
          </Typography>
          <InviteFriendsButton className={classes.emptyStateButton} />
          {!this.isFacebookConnected() && (
            <>
              <Or />
              <Typography variant="subtitle1" color="textSecondary" align="center">
                <T id="friends_facebook_help" />
              </Typography>
              <Button
                color="facebook"
                className={classes.emptyStateButton}
                onClick={this.onFacebookConnectClick}
              >
                <img alt="" src={facebookIconUrl} className={classes.facebookIcon} />
                <T id="friends_facebook_connect" />
              </Button>
            </>
          )}
        </>
      )
      : <MyFriends requests={request} friends={friend} onChallenge={onChallenge} />
  }

  private renderSearch = () =>
    <UserSearchResult users={this.state.searchResult.result!.user} />

  render() {
    const { searchText, friends, searchResult } = this.state
    const hideFacebookConnectButton = !friends.result || this.isFacebookConnected()

    return (
      <>
        <div className={this.props.classes.searchInputContainer}>
          <TextInput
            value={searchText}
            placeholder={this.props.intl.formatMessage({ id: 'friends_search_placeholder' })}
            icon={<SearchIcon className={this.props.classes.searchIcon} />}
            onChange={(text) => { this.setState({ searchText: text }) }}
          />
        </div>
        {!hideFacebookConnectButton && !this.shouldRenderEmptyState() && (
          <FacebookConnect onClick={this.onFacebookConnectClick} />
        )}
        {this.state.searchText.length > 0
          ? (
            <ActionContext
              isLoading={searchResult.isLoading}
              errorCode={searchResult.errorCode}
              component={this.renderSearch}
              componentProps={{}}
            />
          )
          : (
            <ActionContext
              isLoading={friends.isLoading}
              errorCode={friends.errorCode}
              component={this.renderFriends}
              componentProps={{}}
            />
          )
        }
      </>
    )
  }
}

export default withStyles(styles)(injectIntl(MyFriendsTab))
